import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { BLOG_T } from "@/lib/blogTranslations";
import { BLOG_POSTS, CATEGORIES } from "@/lib/blogPosts";
import { BRAND } from "@/lib/config";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import LeadMagnetCard from "@/components/landing/LeadMagnetCard";
import { MagazineBanner } from "@/components/landing/MagazineBanner";
import { formatBlogDate } from "@/lib/formatDate";
import { useCanonical } from "@/lib/useCanonical";
import { ArrowRight, Search as SearchIcon, X } from "lucide-react";

const formatDate = (iso, lang) => formatBlogDate(iso, lang);
const PUBLISHED = BLOG_POSTS.filter((p) => !p.draft);

export default function Blog() {
  const { lang } = useLanguage();
  const t = BLOG_T[lang] || BLOG_T.pt;
  const [query, setQuery] = useState("");
  useCanonical("/blog");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PUBLISHED;
    return PUBLISHED.filter((p) => {
      const i18n = p.i18n[lang] || p.i18n.fr || p.i18n.pt;
      const cat = CATEGORIES[p.category]?.[lang] || p.category;
      return (
        (i18n.title || "").toLowerCase().includes(q) ||
        (i18n.excerpt || "").toLowerCase().includes(q) ||
        (cat || "").toLowerCase().includes(q)
      );
    });
  }, [query, lang]);

  const sorted = [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));
  const [featured, ...rest] = sorted;
  const isSearching = query.trim().length > 0;

  // Breadcrumb + Blog (ItemList) JSON-LD — surfaces the article index in
  // Google's Top Stories carousel and AI Overview "related articles".
  useEffect(() => {
    const url = "https://npdlbr.com.br/blog";
    const blogLabel =
      lang === "en"
        ? "Blog · French articles"
        : lang === "fr"
        ? "Blog · articles de français"
        : "Blog · artigos de francês";
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: "https://npdlbr.com.br/" },
        { "@type": "ListItem", position: 2, name: blogLabel, item: url },
      ],
    };
    const blogList = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "@id": `${url}#blog`,
      url,
      name: blogLabel,
      inLanguage: lang,
      publisher: {
        "@type": "Organization",
        name: "Na Ponta Da Língua Brasil",
        url: "https://npdlbr.com.br",
      },
      blogPost: PUBLISHED.slice(0, 10).map((p) => {
        const pi = p.i18n[lang] || p.i18n.fr || p.i18n.pt;
        return {
          "@type": "BlogPosting",
          headline: pi.title,
          url: `https://npdlbr.com.br/blog/${p.slug}`,
          datePublished: p.date,
          image: p.coverImage,
        };
      }),
    };
    const emit = (obj, tag) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.dynamic = tag;
      el.textContent = JSON.stringify(obj);
      document.head.appendChild(el);
      return el;
    };
    const els = [emit(breadcrumb, "blog-breadcrumb"), emit(blogList, "blog-list")];
    return () => els.forEach((el) => el.remove());
  }, [lang]);

  const searchPh =
    lang === "en"
      ? "Search articles…"
      : lang === "fr"
      ? "Rechercher un article…"
      : "Buscar artigos…";
  const noResults =
    lang === "en"
      ? "No article matches your search."
      : lang === "fr"
      ? "Aucun article ne correspond."
      : "Nenhum artigo corresponde à sua busca.";
  const resultsLabel =
    lang === "en"
      ? "results"
      : lang === "fr"
      ? "résultats"
      : "resultados";

  return (
    <div data-testid="blog-page" className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a]">
      <header className="border-b border-[#e5e3db]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#1e2a5a] text-[#1e2a5a] font-display italic">
              L
            </span>
            <span className="leading-tight">
              <span className="block font-display text-sm sm:text-base">
                {BRAND.name}
              </span>
              <span className="block text-[10px] uppercase tracking-[0.22em] text-[#d9381e]">
                Blog
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/" className="text-xs sm:text-sm link-underline">
              {t.nav.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 py-16 sm:py-24">
        <p className="overline">{t.list.overline}</p>
        <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
          {t.list.title}
        </h1>
        <p className="mt-6 text-base sm:text-lg text-[#5c5c5c] leading-relaxed max-w-2xl">
          {t.list.subtitle}
        </p>
        <MagazineBanner lang={lang} testId="blog-magazine-banner-top" />

        {/* Search bar */}
        <div className="mt-10 max-w-2xl">
          <label htmlFor="blog-search" className="sr-only">{searchPh}</label>
          <div className="relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5c5c5c]" strokeWidth={1.75} />
            <input
              id="blog-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPh}
              data-testid="blog-search-input"
              className="w-full rounded-full bg-white border border-[#e5e3db] pl-11 pr-11 py-3 text-sm text-[#1e2a5a] placeholder:text-[#5c5c5c] focus:border-[#d9381e] focus:outline-none focus:ring-2 focus:ring-[#d9381e]/15 transition-colors"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                data-testid="blog-search-clear"
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-full text-[#5c5c5c] hover:text-[#1e2a5a] hover:bg-[#f0efea] transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {isSearching && (
            <p className="mt-3 text-xs uppercase tracking-[0.18em] text-[#5c5c5c]" data-testid="blog-search-count">
              {filtered.length} {resultsLabel}
            </p>
          )}
        </div>

        {/* No matches state */}
        {sorted.length === 0 && (
          <p
            className="mt-16 text-center text-base text-[#5c5c5c] italic"
            data-testid="blog-search-empty"
          >
            {noResults}
          </p>
        )}

        {/* Featured (only when NOT searching, to keep list dense during search) */}
        {featured && !isSearching && (
          <FeaturedCard post={featured} lang={lang} t={t} />
        )}

        {/* Lead magnet card — between featured and rest (hidden during active search) */}
        {!isSearching && <LeadMagnetCard context="blog_index" />}

        {/* Rest */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {(isSearching ? sorted : rest).map((post) => (
            <PostCard key={post.slug} post={post} lang={lang} t={t} />
          ))}
        </div>
        <MagazineBanner lang={lang} testId="blog-magazine-banner-bottom" />
      </main>
    </div>
  );
}

const FeaturedCard = ({ post, lang, t }) => {
  const i18n = post.i18n[lang] || post.i18n.fr || post.i18n.pt;
  const cat = CATEGORIES[post.category]?.[lang] || post.category;
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-featured-${post.slug}`}
      className="mt-16 grid lg:grid-cols-12 gap-8 lg:gap-12 group"
    >
      <div className="lg:col-span-7 relative overflow-hidden bg-[#f0efea] aspect-[16/9] lg:aspect-[4/3] border border-[#e5e3db] rounded-xl">
        <img
          src={post.coverImage}
          alt={i18n.title}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="lg:col-span-5 flex flex-col justify-center">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em]">
          <span className="text-[#d9381e] font-medium">{cat}</span>
          <span className="text-[#5c5c5c]">·</span>
          <span className="text-[#5c5c5c]">{formatDate(post.date, lang)}</span>
          <span className="text-[#5c5c5c]">·</span>
          <span className="text-[#5c5c5c]">{post.readTime} {t.list.minRead}</span>
        </div>
        <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1e2a5a] leading-[1.05]">
          {i18n.title}
        </h2>
        <p className="mt-5 text-base text-[#5c5c5c] leading-relaxed max-w-md">
          {i18n.excerpt}
        </p>
        <span className="inline-flex items-center gap-2 mt-8 text-sm font-medium link-underline w-fit">
          {t.list.read}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};

const PostCard = ({ post, lang, t }) => {
  const i18n = post.i18n[lang] || post.i18n.fr || post.i18n.pt;
  const cat = CATEGORIES[post.category]?.[lang] || post.category;
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-post-${post.slug}`}
      className="group flex flex-col"
    >
      <div className="relative overflow-hidden bg-[#f0efea] aspect-[4/3] border border-[#e5e3db] rounded-xl">
        <img
          src={post.coverImage}
          alt={i18n.title}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-[0.18em]">
        <span className="text-[#d9381e] font-medium">{cat}</span>
        <span className="text-[#5c5c5c]">·</span>
        <span className="text-[#5c5c5c]">{post.readTime} {t.list.minRead}</span>
      </div>
      <h3 className="font-display mt-3 text-2xl text-[#1e2a5a] leading-snug">
        {i18n.title}
      </h3>
      <p className="mt-3 text-sm text-[#5c5c5c] leading-relaxed">{i18n.excerpt}</p>
      <span className="text-xs text-[#5c5c5c] mt-3">{formatDate(post.date, lang)}</span>
    </Link>
  );
};
